import { Component, OnInit, ChangeDetectorRef, OnDestroy, HostListener, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { DocumentModelService } from '../../services/document-model.service';
import { TruthManagement } from '../../services/truth-management';
import { ModalService } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { ExtractionRunService, ExtractionRun } from '../../services/extraction-run.service';
import { Subject, interval, Subscription } from 'rxjs';
import { takeUntil, switchMap, tap } from 'rxjs/operators';
import { PageHeaderComponent } from '../page-header/page-header.component';

interface ExtractionResult {
  request_id: string;
  status: string;
  data?: Record<string, any>;
  error?: string;
}

interface DocumentModel {
  id: string;
  name: string;
  code: string;
}

interface ExtractionRunExtended extends ExtractionRun {
  isDataReceived?: boolean; // Track if API has returned data for this run
}

@Component({
  selector: 'app-data-extraction-page',
  imports: [CommonModule, FormsModule, PageHeaderComponent],
  templateUrl: './data-extraction-page.html',
  styleUrls: ['./data-extraction-page.scss'],
})
export class DataExtractionPageComponent implements OnInit, OnDestroy {
  // Placeholder text
  pdfPlaceholder = 'Upload a PDF file';

  // File upload
  selectedFile: File | null = null;
  fileInputError: string = '';

  // Form fields
  selectedDocumentModel: string = '';
  selectedOcrMode: string = '';
  selectedLlmModel: string = '';

  // Document models list
  documentModels: DocumentModel[] = [];
  isModelsLoading: boolean = false;
  modelsLoadError: string = '';

  // OCR modes
  ocrModes: string[] = [];

  // LLM models
  llmModels: string[] = [];

  // Recent extraction runs (session-scoped via ExtractionRunService)
  recentRuns: ExtractionRun[] = [];

  // Loading state
  isExtracting: boolean = false;
  loadingFromAPI: boolean = false;

  // Sorting properties
  sortColumn: 'fileName' | 'pages' | 'documentModel' | 'ocrMode' | 'llmModel' | 'status' | 'startedDate' | 'duration' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // Searchable dropdown properties
  documentModelSearch: string = '';
  ocrModeSearch: string = '';
  llmModelSearch: string = '';

  // Track search mode for each dropdown (when open and user can type)
  documentModelSearchMode: boolean = false;
  ocrModeSearchMode: boolean = false;
  llmModelSearchMode: boolean = false;

  filteredDocumentModels: DocumentModel[] = [];
  filteredOcrModes: string[] = [];
  filteredLlmModels: string[] = [];

  openDropdown: 'documentModel' | 'ocrMode' | 'llmModel' | null = null;

  // Polling state
  private pollingSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();
  private pollInterval = 3000; // Poll every 3 seconds for status only
  
  // Timer state - maps runId to formatted duration string
  private runTimers: Map<string, string> = new Map();

  // Role-based access control for OCR and LLM dropdowns
  canEditOcrLlmOptions: boolean = false;

  // Current client ID for filtering document models
  private currentClientId: string | null = null;

  constructor(
    public router: Router,
    public authService: AuthService,
    private documentModelService: DocumentModelService,
    private truthManagement: TruthManagement,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private extractionRunService: ExtractionRunService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private ngZone: NgZone,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    this.initializeRoleBasedAccess();
    this.subscribeToClientChanges();
    this.subscribeToRecentRuns();
    this.subscribeToTimers();
    this.startPollingExtractionStatus();
  }

  /**
   * Subscribe to client changes and reload document models when client changes
   * Clears selected document model, OCR mode, and LLM model when client changes
   */
  private subscribeToClientChanges(): void {
    this.authService.currentClient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(client => {
        this.currentClientId = client?.client_id || null;
        // Clear selected values when client changes
        this.selectedDocumentModel = '';
        this.selectedOcrMode = '';
        this.selectedLlmModel = '';
        this.loadDocumentModels();
      });
  }

  /**
   * Initialize role-based access control for OCR and LLM dropdowns
   * Only admin and superuser roles can edit these options
   */
  private initializeRoleBasedAccess(): void {
    this.canEditOcrLlmOptions = this.isAdminOrSuperuser();
  }

  /**
   * Check if current user has admin or superuser role
   */
  private isAdminOrSuperuser(): boolean {
    const userRole = sessionStorage.getItem('userRole') || '';
    return userRole.toLowerCase() === 'admin' || userRole.toLowerCase() === 'superuser';
  }

  /**
   * Check if a specific dropdown can be edited by current user
   * Document model is always editable, OCR and LLM modes only for admin/superuser
   */
  canEditDropdown(dropdown: 'documentModel' | 'ocrMode' | 'llmModel'): boolean {
    if (dropdown === 'documentModel') {
      return true; // Document model always editable
    }
    // OCR and LLM dropdowns only editable for admin/superuser
    return this.canEditOcrLlmOptions;
  }

  /**
   * Check if extraction can be submitted (document model selected and file uploaded)
   */
  canExtract(): boolean {
    return !!this.selectedDocumentModel && !!this.selectedFile && !this.isExtracting;
  }

  /**
   * Subscribe to recent runs from the service
   */
  private subscribeToRecentRuns(): void {
    this.extractionRunService.recentRuns$
      .pipe(takeUntil(this.destroy$))
      .subscribe(runs => {
        this.recentRuns = runs;
        this.cdr.markForCheck();
      });
  }

  /**
   * Subscribe to timer updates from the service
   * Updates the local runTimers map when any extraction run timer ticks every 1 second
   * Uses NgZone.run() to ensure Angular detects changes
   */
  private subscribeToTimers(): void {
    this.extractionRunService.timers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(timers => {
        this.ngZone.run(() => {
          this.runTimers = new Map(timers);
          // Force change detection immediately for smooth UI updates
          this.cdr.detectChanges();
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  /**
   * Start polling for extraction status updates
   */
  private startPollingExtractionStatus(): void {
    this.pollingSubscription = interval(this.pollInterval)
      .pipe(
        switchMap(() => {
          // Poll status for all running extractions
          const runningRuns = this.extractionRunService.getRunningExtractions();
          if (runningRuns.length === 0) {
            return Promise.resolve([]);
          }
          
          // Check status for all running runs
          return Promise.all(
            runningRuns.map(run => 
              this.truthManagement.pollExtractionStatus(run.id).toPromise()
                .then(response => ({ ...response, runId: run.id }))
                .catch(error => ({ error, runId: run.id }))
            )
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (results: any[]) => {
          if (Array.isArray(results) && results.length > 0) {
            this.updateRunStatuses(results);
          }
        },
        error: (error) => {
        }
      });
  }

  /**
   * Update extraction run statuses based on API response
   */
  private updateRunStatuses(results: any[]): void {
    const updates: { id: string; data: Partial<ExtractionRun> }[] = [];

    results.forEach(result => {
      if (result.error) {
        return;
      }

      const run = this.extractionRunService.getRunById(result.runId) as ExtractionRunExtended;
      if (!run) return;

      const oldStatus = run.status;
      const updateData: Partial<ExtractionRunExtended> = {};

      // Mark that we've received data from the API
      if (!run.isDataReceived) {
        updateData.isDataReceived = true;
      }

      // Update status based on API response
      if (result.status === 'ACCEPTED' || result.status === 'accepted') {
        updateData.status = 'Running';
      } else if (result.status === 'COMPLETED' || result.status === 'completed') {
        updateData.status = 'Completed';
        // Extract the attributes as extracted data
        updateData.extractedData = result.attributes || result.data || {};
        
        // Show completion toaster when first poll detects COMPLETED status
        if (oldStatus !== 'Completed') {
          this.notificationService.showExtractionCompleted(run.documentModel, run.fileName);
        }
      } else if (result.status === 'FAILED' || result.status === 'failed') {
        updateData.status = 'Failed';
        
        // Show failure toaster when first poll detects FAILED status
        if (oldStatus !== 'Failed') {
          this.notificationService.showError(
            'Data extraction failed',
            [
              { label: 'File', value: run.fileName },
              { label: 'Document Model', value: run.documentModel }
            ]
          );
        }
      }

      // Extract pages from extraction_summary
      if (result.extraction_summary?.pages !== undefined) {
        updateData.pages = result.extraction_summary.pages;
      }

      // Extract final duration from API when extraction completes
      // This ensures the final duration is persisted when the run completes
      if (result.total_time_taken !== undefined) {
        const duration = result.total_time_taken;
        updateData.duration = this.formatDuration(typeof duration === 'string' ? parseFloat(duration) : duration);
        updateData.durationMs = (typeof duration === 'string' ? parseFloat(duration) : duration) * 1000;
      }

      updates.push({ id: result.runId, data: updateData });

      // Status change to Completed - notification will be shown when user navigates to result page
      // and poll_extract API returns the final COMPLETED response
    });

    if (updates.length > 0) {
      this.extractionRunService.updateRuns(updates);
    }
  }

  /**
   * Show extraction results in a modal
   */
  private showCompletionModal(run: ExtractionRun): void {
    const dataDisplay = this.formatExtractedData(run.extractedData);
    
    this.modalService.show({
      type: 'alert' as any,
      title: 'Extraction Completed',
      message: `Data extraction completed for "${run.fileName}".`,
      confirmText: 'Close',
      details: [
        { label: 'File', value: run.fileName },
        { label: 'Document Model', value: run.documentModel },
        { label: 'Status', value: 'Completed' },
        { label: 'Extracted Data', value: dataDisplay }
      ]
    });
  }

  /**
   * Format extracted data for display
   */
  private formatExtractedData(data?: Record<string, any>): string {
    if (!data) return 'No data extracted';
    
    try {
      return JSON.stringify(data, null, 2).substring(0, 500) + '...';
    } catch {
      return 'Unable to display data';
    }
  }

  /**
   * Load document models from API with current client ID filter
   */
  private loadDocumentModels(): void {
    // Only load if we have a client ID
    if (!this.currentClientId) {
      this.documentModels = [];
      this.initializeFilteredOptions();
      return;
    }

    this.isModelsLoading = true;
    this.modelsLoadError = '';

    this.documentModelService.getAdminDocuments(this.currentClientId).subscribe({
      next: (response: any) => {
        // Extract document_models array from the API response
        // API returns: { document_models: ["model1", "model2", ...], count: 107 }
        const models = response?.document_models || [];
        
        // Map string array to DocumentModel interface
        this.documentModels = Array.isArray(models) ? models.map((modelCode: string) => ({
          id: modelCode,
          name: modelCode,
          code: modelCode
        })) : [];
        
        this.initializeFilteredOptions();
        this.isModelsLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isModelsLoading = false;
      }
    });
  }

  /**
   * Handle document model selection and load its OCR mode and LLM model configuration
   */
  onDocumentModelChanged(documentModel: string): void {
    if (!documentModel) {
      return;
    }
    // Fetch document model config to get configured values
    this.documentModelService.getDocumentModelConfig(documentModel).subscribe({
      next: (config: any) => {
        // Use configured values if they exist, otherwise use defaults
        this.selectedOcrMode = config?.ocr_mode || 'ocr';
        this.selectedLlmModel = config?.llm_model || 'gpt-5.2';
        // Fetch available options for dropdowns
        this.loadDropdownOptions();
        
        // Force change detection to update UI
        this.cdr.detectChanges();
      },
      error: (error) => {
        // Use defaults on error
        this.selectedOcrMode = 'ocr';
        this.selectedLlmModel = 'gpt-5.2';
        // Still try to load dropdown options
        this.loadDropdownOptions();
        
        // Force change detection to update UI
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load available OCR modes and LLM models from dropdown config
   */
  private loadDropdownOptions(): void {
    this.documentModelService.getAdminDropdownConfig().subscribe({
      next: (dropdownConfig: any) => {
        // Populate dropdown arrays with all available options
        this.ocrModes = dropdownConfig?.ocr_modes || [this.selectedOcrMode];
        this.llmModels = dropdownConfig?.llm_models || [this.selectedLlmModel];
        this.initializeFilteredOptions();
        this.cdr.markForCheck();
      },
      error: (error) => {
        // Fallback: show only the currently selected values
        this.ocrModes = [this.selectedOcrMode];
        this.llmModels = [this.selectedLlmModel];
        this.initializeFilteredOptions();
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Handle PDF file selection from input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      if (!file.type.includes('pdf')) {
        this.notificationService.showError('Non pdf file upload not allowed');
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
      this.fileInputError = '';
    }
  }

  /**
   * Trigger file input click
   */
  triggerFileInput(): void {
    const fileInput = document.getElementById('pdf-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Get selected file name for display (truncated)
   */
  getSelectedFileName(): string {
    if (this.selectedFile) {
      return this.truncateFileName(this.selectedFile.name);
    }
    return this.pdfPlaceholder;
  }

  /**
   * Truncate file name to max 40 characters, preserving extension
   */
  private truncateFileName(fileName: string, maxLength: number = 40): string {
    if (fileName.length <= maxLength) {
      return fileName;
    }
    
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension, just truncate
      return fileName.substring(0, maxLength - 3) + '...';
    }
    
    const extension = fileName.substring(lastDotIndex);
    const nameWithoutExt = fileName.substring(0, lastDotIndex);
    const availableSpace = maxLength - extension.length - 3; // 3 for '...'
    
    if (availableSpace > 0) {
      return nameWithoutExt.substring(0, availableSpace) + '...' + extension;
    }
    
    // If extension is too long, just truncate everything
    return fileName.substring(0, maxLength - 3) + '...';
  }

  /**
   * Extract data from PDF
   */
  extractData(): void {
    // Validate inputs
    if (!this.selectedFile) {
      this.fileInputError = 'Please select a PDF file';
      return;
    }

    if (!this.selectedDocumentModel) {
      this.fileInputError = 'Please select a document model before extracting data';
      return;
    }

    // Submit extraction directly without confirmation modal
    this.submitExtraction();
  }

  /**
   * Submit extraction to API after confirmation
   */
  private submitExtraction(): void {
    if (!this.selectedFile) {
      return;
    }

    this.isExtracting = true;

    // Call the actual API to submit extraction with llm_model and ocr_mode
    this.truthManagement.submitDataExtraction(
      this.selectedDocumentModel,
      this.selectedFile,
      this.selectedFile.name,
      this.selectedLlmModel,
      this.selectedOcrMode
    ).subscribe({
      next: (response: any) => {
        // Create new extraction run entry
        const newRun: ExtractionRunExtended = {
          id: response?.request_id || this.generateUUID(),
          fileName: this.selectedFile!.name,
          pages: undefined, // Will be populated by API
          documentModel: this.selectedDocumentModel,
          ocrMode: this.selectedOcrMode,
          llmModel: this.selectedLlmModel,
          status: 'Running',
          startedDate: new Date(),
          duration: '', // Will be populated by API
          durationMs: 0,
          isDataReceived: false // Track that we're waiting for API data
        };

        // Add run to service (persists across navigation)
        this.extractionRunService.addRun(newRun);

        // Show toaster success notification
        this.notificationService.showSuccess(
          'Data extraction started',
          [
            { label: 'File', value: this.selectedFile!.name },
            { label: 'Document Model', value: this.selectedDocumentModel }
          ]
        );

        // Reset form
        this.selectedFile = null;
        this.selectedDocumentModel = '';
        this.selectedOcrMode = '';
        this.selectedLlmModel = '';
        this.isExtracting = false;

        // Reset file input
        const fileInput = document.getElementById('pdf-file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }

        this.cdr.markForCheck();
      },
      error: (error) => {
        this.notificationService.showError(
          'Failed to submit extraction',
          [{ label: 'File', value: this.selectedFile?.name || 'Unknown' }]
        );
        this.isExtracting = false;
        this.cdr.markForCheck();
      }
    });
  }



  /**
   * Handle action button click - navigate to result page if completed
   */
  viewRunDetails(run: ExtractionRun): void {
    if (run.status === 'Completed') {
      // Navigate to result page - store selected run in service first
      this.extractionRunService.setSelectedRun(run);
      this.extractionRunService.setNavigationSource('data-extraction');
      this.router.navigate(['/data_extraction_result']);
    } else if (run.status === 'Failed') {
      this.modalService.show({
        type: 'alert' as any,
        title: 'Extraction Failed',
        message: `Data extraction failed for "${run.fileName}". Please try again.`,
        confirmText: 'OK'
      });
    }
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string): string {
    return status.toLowerCase();
  }

  /**
   * Get user email from auth service
   */
  /**
   * Navigate to home dashboard
   */
  navigateHome(): void {
    this.router.navigate(['/home']);
  }

  /**
   * Navigate to admin settings
   */
  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  /**
   * Navigate to stats dashboard
   */
  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  /**
   * Navigate to operations request analytics
   */
  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  /**
   * Generate UUID for new extraction runs
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Map API status values to component status display
   */
  private mapApiStatus(apiStatus: string): 'Running' | 'Completed' | 'Failed' | 'Pending' {
    if (!apiStatus) {
      return 'Pending';
    }

    const status = apiStatus.toUpperCase();
    
    if (status === 'ACCEPTED') {
      return 'Running';
    } else if (status === 'COMPLETED') {
      return 'Completed';
    } else if (status === 'FAILED') {
      return 'Failed';
    }
    
    return 'Pending';
  }

  /**
   * Public wrapper for truncateFileName for template use
   */
  truncateFileNameDisplay(fileName: string): string {
    return this.truncateFileName(fileName, 40);
  }

  /**
   * Get/set display value for document model dropdown
   * Always shows selected model, never shows search term
   * Search term is handled separately in dropdown search input
   */
  get documentModelDisplay(): string {
    return this.selectedDocumentModel || '';
  }

  set documentModelDisplay(value: string) {
    // Display is read-only, only updated via selectOption
  }

  /**
   * Get/set display value for OCR mode dropdown
   * Always shows the auto-populated value in the input field for all roles
   * Search term is handled separately in dropdown search input
   */
  get ocrModeDisplay(): string {
    // Return empty if no document model selected (to show placeholder)
    if (!this.selectedDocumentModel) {
      return '';
    }
    // Always show selected value in input field for all roles
    return this.selectedOcrMode || '';
  }

  set ocrModeDisplay(value: string) {
    // Display is read-only, only updated via selectOption
  }

  /**
   * Get/set display value for LLM model dropdown
   * Always shows the auto-populated value in the input field for all roles
   * Search term is handled separately in dropdown search input
   */
  get llmModelDisplay(): string {
    // Return empty if no document model selected (to show placeholder)
    if (!this.selectedDocumentModel) {
      return '';
    }
    // Always show selected value in input field for all roles
    return this.selectedLlmModel || '';
  }

  set llmModelDisplay(value: string) {
    // Display is read-only, only updated via selectOption
  }

  /**
   * Get the display duration for a run from the local timers map
   * If not in timer map (completed/failed), returns run.duration
   * This enables smooth 1-second timer updates while running
   */
  getRunDuration(run: ExtractionRun): string {
    // If timer is active for this run, use smooth timer value
    const timerValue = this.runTimers.get(run.id);
    if (timerValue) {
      return timerValue;
    }
    // Otherwise use the duration from the run (set by API for completed runs)
    return run.duration || '-';
  }

  /**
   * Format duration seconds to human-readable string
   * Rounds all values to whole numbers with no decimals
   * Examples: "5s", "1m 18s", "2h 15m 45s"
   */
  private formatDuration(seconds: number): string {
    if (seconds <= 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }



  /**
   * Handle column sorting
   */
  sortBy(column: 'fileName' | 'pages' | 'documentModel' | 'ocrMode' | 'llmModel' | 'status' | 'startedDate' | 'duration'): void {
    if (this.sortColumn === column) {
      // Toggle direction if clicking the same column
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to ascending
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    
    this.applySorting();
    this.cdr.detectChanges();
  }

  /**
   * Apply sorting to recent runs
   */
  applySorting(): void {
    if (!this.sortColumn) return;
    
    this.recentRuns.sort((a, b) => {
      let compareValue = 0;
      
      switch (this.sortColumn) {
        case 'fileName':
          compareValue = (a.fileName || '').localeCompare(b.fileName || '');
          break;
        case 'pages':
          compareValue = (a.pages || 0) - (b.pages || 0);
          break;
        case 'documentModel':
          compareValue = (a.documentModel || '').localeCompare(b.documentModel || '');
          break;
        case 'ocrMode':
          compareValue = (a.ocrMode || '').localeCompare(b.ocrMode || '');
          break;
        case 'llmModel':
          compareValue = (a.llmModel || '').localeCompare(b.llmModel || '');
          break;
        case 'status':
          compareValue = (a.status || '').localeCompare(b.status || '');
          break;
        case 'startedDate':
          const aTime = a.startedDate ? new Date(a.startedDate).getTime() : 0;
          const bTime = b.startedDate ? new Date(b.startedDate).getTime() : 0;
          compareValue = aTime - bTime;
          break;
        case 'duration':
          compareValue = (a.durationMs || 0) - (b.durationMs || 0);
          break;
      }
      
      return this.sortDirection === 'asc' ? compareValue : -compareValue;
    });
  }

  /**
   * Get sort icon for column header
   */
  getSortIcon(column: 'fileName' | 'pages' | 'documentModel' | 'ocrMode' | 'llmModel' | 'status' | 'startedDate' | 'duration'): SafeHtml {
    if (this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>&#9650;</span><span>&#9660;</span></div>');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '&#9650;' : '&#9660;');
  }

  /**
   * Fuzzy match algorithm - returns true if search string matches the option
   */
  private fuzzyMatch(searchTerm: string, option: string): boolean {
    const search = searchTerm.toLowerCase().trim();
    const opt = option.toLowerCase();
    
    if (!search) return true; // Empty search matches everything
    
    let searchIndex = 0;
    for (let i = 0; i < opt.length && searchIndex < search.length; i++) {
      if (opt[i] === search[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === search.length;
  }

  /**
   * Handle dropdown input focus - opens dropdown and initializes filtered options
   * Resets search to empty to show all options by default
   * Prevents opening OCR and LLM dropdowns for non-admin users
   */
  onDropdownFocus(dropdown: 'documentModel' | 'ocrMode' | 'llmModel'): void {
    // Prevent opening OCR/LLM dropdowns for non-admin users
    if (!this.canEditDropdown(dropdown)) {
      return;
    }
    
    this.openDropdown = dropdown;
    
    // Enter search mode and reset search to empty string
    // This shows all options by default without requiring user to clear the selected value
    if (dropdown === 'documentModel') {
      this.documentModelSearchMode = true;
      this.documentModelSearch = ''; // Reset search to empty
    } else if (dropdown === 'ocrMode') {
      this.ocrModeSearchMode = true;
      this.ocrModeSearch = ''; // Reset search to empty
    } else if (dropdown === 'llmModel') {
      this.llmModelSearchMode = true;
      this.llmModelSearch = ''; // Reset search to empty
    }
    
    this.updateFilteredOptions(dropdown);
  }

  /**
   * Handle dropdown input blur - closes dropdown and clears search
   * Only closes if focus moves outside the dropdown container
   * Allows users to type in the search input without closing dropdown
   */
  onDropdownBlur(): void {
    // Delay closing to allow click event on option items to fire
    // and to check if focus moved to search input inside dropdown
    setTimeout(() => {
      // Check if focused element is inside the dropdown
      const focusedElement = document.activeElement;
      const dropdownElement = document.querySelector('.searchable-select-dropdown');
      
      // If focus is still within the dropdown container, don't close
      if (dropdownElement?.contains(focusedElement)) {
        return;
      }
      
      // Close the dropdown only if focus moved completely outside
      this.openDropdown = null;
      
      // Exit search mode and clear search for all dropdowns
      this.documentModelSearchMode = false;
      this.ocrModeSearchMode = false;
      this.llmModelSearchMode = false;
      this.documentModelSearch = '';
      this.ocrModeSearch = '';
      this.llmModelSearch = '';
    }, 200);
  }

  /**
   * Update filtered options based on search term
   */
  updateFilteredOptions(dropdown: 'documentModel' | 'ocrMode' | 'llmModel'): void {
    if (dropdown === 'documentModel') {
      this.filteredDocumentModels = this.documentModels.filter(model =>
        this.fuzzyMatch(this.documentModelSearch, model.name)
      );
    } else if (dropdown === 'ocrMode') {
      this.filteredOcrModes = this.ocrModes.filter(mode =>
        this.fuzzyMatch(this.ocrModeSearch, mode)
      );
    } else if (dropdown === 'llmModel') {
      this.filteredLlmModels = this.llmModels.filter(model =>
        this.fuzzyMatch(this.llmModelSearch, model)
      );
    }
  }

  /**
   * Handle search input change
   */
  onSearchChange(dropdown: 'documentModel' | 'ocrMode' | 'llmModel'): void {
    this.updateFilteredOptions(dropdown);
  }

  /**
   * Select option from dropdown
   * Prevents changing OCR and LLM options for non-admin users
   * Clears search input, exits search mode, and closes dropdown immediately
   */
  selectOption(dropdown: 'documentModel' | 'ocrMode' | 'llmModel', value: string): void {
    // Prevent selecting options in OCR/LLM dropdowns for non-admin users
    if (!this.canEditDropdown(dropdown)) {
      return;
    }
    
    if (dropdown === 'documentModel') {
      this.selectedDocumentModel = value;
      this.documentModelSearch = '';
      this.documentModelSearchMode = false;
      this.onDocumentModelChanged(value);
    } else if (dropdown === 'ocrMode') {
      this.selectedOcrMode = value;
      this.ocrModeSearch = '';
      this.ocrModeSearchMode = false;
    } else if (dropdown === 'llmModel') {
      this.selectedLlmModel = value;
      this.llmModelSearch = '';
      this.llmModelSearchMode = false;
    }
    
    // Close dropdown immediately (no timeout delay like blur)
    this.openDropdown = null;
    this.initializeFilteredOptions();
  }

  /**
   * Get placeholder text for dropdown input
   * Shows selected value if available, otherwise shows hint text
   */
  getDropdownPlaceholder(dropdown: 'documentModel' | 'ocrMode' | 'llmModel'): string {
    if (dropdown === 'documentModel') {
      return 'Select a document model';
    } else if (dropdown === 'ocrMode') {
      return this.selectedDocumentModel ? 'Select OCR mode' : 'OCR mode';
    } else if (dropdown === 'llmModel') {
      return this.selectedDocumentModel ? 'Select LLM model' : 'LLM model';
    }
    return '';
  }

  /**
   * Get OCR Mode placeholder - hint text based on role
   * Placeholder won't be visible when there's an auto-populated value in the field
   */
  getOcrModePlaceholder(): string {
    if (!this.selectedDocumentModel) {
      // Before document model selection: different text for admin vs non-admin
      return this.canEditOcrLlmOptions ? 'Select OCR mode' : 'OCR mode';
    }
    // For admin users: show search/select placeholder
    if (this.canEditOcrLlmOptions) {
      return 'Select OCR mode';
    }
    // For non-admin users: show static placeholder (rarely visible since field has value)
    return 'OCR mode';
  }

  /**
   * Get LLM Model placeholder - hint text based on role
   * Placeholder won't be visible when there's an auto-populated value in the field
   */
  getLlmModelPlaceholder(): string {
    if (!this.selectedDocumentModel) {
      // Before document model selection: different text for admin vs non-admin
      return this.canEditOcrLlmOptions ? 'Select LLM model' : 'LLM model';
    }
    // For admin users: show search/select placeholder
    if (this.canEditOcrLlmOptions) {
      return 'Select LLM model';
    }
    // For non-admin users: show static placeholder (rarely visible since field has value)
    return 'LLM model';
  }



  /**
   * Close dropdown when clicking outside
   * Note: This is a fallback; blur event handles most cases
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const selectContainers = document.querySelectorAll('.searchable-select-container');
    
    let clickedInside = false;
    selectContainers.forEach(container => {
      if (container.contains(target)) {
        clickedInside = true;
      }
    });
    
    if (!clickedInside && this.openDropdown) {
      this.openDropdown = null;
      // Reset all search fields
      this.documentModelSearch = '';
      this.ocrModeSearch = '';
      this.llmModelSearch = '';
      this.initializeFilteredOptions();
    }
  }

  /**
   * Initialize filtered options when dropdown data changes
   */
  private initializeFilteredOptions(): void {
    this.filteredDocumentModels = [...this.documentModels];
    this.filteredOcrModes = [...this.ocrModes];
    this.filteredLlmModels = [...this.llmModels];
  }
}
